import React from 'react';
import {
    Box,
    Checkbox,
    Chip,
    Grid2,
    IconButton,
    Stack,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useTranslation } from 'react-i18next';
import SkeletonTable from '../../../components/atom/SkeletonTable';
import SortableTableCell from '../../../components/atom/SortableTableCell';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import { TABLE_ROW_COUNT_OPTIONS } from '../../../helpers/constants';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';
import { handleChangeRowsPerPage, handlePageChange } from '../../../helpers/handleTableFunctions';

function WaitlistSubscriptionsTable({
    rows,
    total,
    page,
    limit,
    setParams,
    loading,
    selectedIds,
    setSelectedIds,
    onSendReminder,
    onDelete,
    onConvertToBooking,
    sendingReminderIds,
    deletingId,
    convertingId,
    sort,
    sortDir,
    onSort,
    hasActiveFilters = false,
    showTimeslotColumn = false,
}) {
    const { t } = useTranslation();
    const { showModal } = useModals();
    const selectedIdSet = new Set(selectedIds);
    const allRowsSelected = rows.length > 0 && rows.every((row) => selectedIdSet.has(row.id));
    const hasSomeRowsSelected = rows.some((row) => selectedIdSet.has(row.id));
    const toggleRowSelection = (rowId) => {
        if (selectedIdSet.has(rowId)) {
            setSelectedIds(selectedIds.filter((id) => id !== rowId));
            return;
        }
        setSelectedIds([...selectedIds, rowId]);
    };

    const togglePageSelection = () => {
        if (allRowsSelected) {
            setSelectedIds(selectedIds.filter((id) => !rows.some((row) => row.id === id)));
            return;
        }

        const nextSelectedIds = new Set(selectedIds);
        rows.forEach((row) => {
            nextSelectedIds.add(row.id);
        });
        setSelectedIds([...nextSelectedIds]);
    };

    return (
        <Grid2 size={{ xs: 12 }}>
            <TableContainer sx={{ maxHeight: { xs: '60dvh', md: 'calc(100dvh - 256px)' } }}>
                <Table stickyHeader sx={{ minWidth: 900 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    size="small"
                                    checked={allRowsSelected}
                                    indeterminate={!allRowsSelected && hasSomeRowsSelected}
                                    onChange={togglePageSelection}
                                />
                            </TableCell>
                            <SortableTableCell
                                arrow={sort === 'name' ? sortDir : ''}
                                onClick={() => onSort('name')}
                            >
                                {t('waitlist.columns.subscriberDetails')}
                            </SortableTableCell>
                            {showTimeslotColumn ? (
                                <TableCell>{t('waitlist.columns.timeslot')}</TableCell>
                            ) : null}
                            <SortableTableCell
                                arrow={sort === 'requested_total' ? sortDir : ''}
                                onClick={() => onSort('requested_total')}
                            >
                                {t('waitlist.columns.requested')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'fulfillable' ? sortDir : ''}
                                onClick={() => onSort('fulfillable')}
                            >
                                {t('waitlist.columns.fulfillability')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'last_notified_at' ? sortDir : ''}
                                onClick={() => onSort('last_notified_at')}
                            >
                                {t('waitlist.columns.activity')}
                            </SortableTableCell>
                            <TableCell align="right">{t('waitlist.columns.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <SkeletonTable
                                rows={Math.max(limit, 5)}
                                cells={showTimeslotColumn ? 7 : 6}
                            />
                        ) : (
                            rows.map((row) => {
                                const isSendingReminder = sendingReminderIds.includes(row.id);
                                const reminderDisabled = !row.isFulfillable || isSendingReminder;

                                return (
                                    <TableRow key={row.id} hover>
                                        <TableCell
                                            padding="checkbox"
                                            sx={{ verticalAlign: 'middle' }}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={selectedIdSet.has(row.id)}
                                                onChange={() => toggleRowSelection(row.id)}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'middle' }}>
                                            <Stack spacing={0.5}>
                                                <Stack
                                                    direction="row"
                                                    spacing={0.5}
                                                    alignItems="center"
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        sx={{ fontWeight: 700 }}
                                                    >
                                                        {row.name}
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="body2" color="text.secondary">
                                                    {row.email}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {row.phone || t('waitlist.labels.noPhone')}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        {showTimeslotColumn ? (
                                            <TableCell sx={{ verticalAlign: 'middle' }}>
                                                <Typography variant="body2">
                                                    {row.timeslotStartTime
                                                        ? formatDateTimeInVenueTz(
                                                              row.timeslotStartTime,
                                                          )
                                                        : ''}
                                                </Typography>
                                            </TableCell>
                                        ) : null}
                                        <TableCell sx={{ verticalAlign: 'middle' }}>
                                            <Stack spacing={0.75}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 700 }}
                                                >
                                                    {t('waitlist.labels.ticketsTotal', {
                                                        count: row.requestedTicketsTotal,
                                                    })}
                                                </Typography>
                                                <Stack
                                                    direction="row"
                                                    spacing={0.75}
                                                    useFlexGap
                                                    flexWrap="wrap"
                                                >
                                                    {row.ticketComposition.map((item) => (
                                                        <Chip
                                                            key={`${row.id}-${item.ticketTypeId}-${item.ticketOptionId}`}
                                                            size="small"
                                                            variant="outlined"
                                                            label={item.label}
                                                        />
                                                    ))}
                                                </Stack>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'middle' }}>
                                            {row.isFulfillable ? (
                                                <Chip
                                                    size="small"
                                                    label={t('waitlist.status.ready')}
                                                    color="success"
                                                    sx={{ minWidth: 130, justifyContent: 'center' }}
                                                />
                                            ) : (
                                                <Chip
                                                    size="small"
                                                    label={t('waitlist.status.notEnoughSeats')}
                                                    color="error"
                                                    sx={{ minWidth: 130, justifyContent: 'center' }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'middle' }}>
                                            {row.lastNotifiedAt ? (
                                                <Stack spacing={0.5}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        {t('waitlist.activity.lastNotified', {
                                                            date: formatDateTimeInVenueTz(
                                                                row.lastNotifiedAt,
                                                            ),
                                                        })}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {t('waitlist.activity.sentTimes', {
                                                            count: row.notificationCount,
                                                        })}
                                                    </Typography>
                                                </Stack>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        display: 'inline-flex',
                                                        flexDirection: 'column',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Chip
                                                        size="small"
                                                        label={t('waitlist.status.neverNotified')}
                                                        color="warning"
                                                        sx={{
                                                            minWidth: 130,
                                                            justifyContent: 'center',
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {t('waitlist.activity.added', {
                                                            date: formatDateTimeInVenueTz(
                                                                row.createdAt,
                                                            ),
                                                        })}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                justifyContent="flex-end"
                                                alignItems="center"
                                            >
                                                <Tooltip
                                                    title={t('waitlist.actions.convertToBooking')}
                                                >
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            disabled={
                                                                !row.isFulfillable ||
                                                                convertingId === row.id
                                                            }
                                                            onClick={() => {
                                                                showModal(MODAL_TYPE.CONFIRMATION, {
                                                                    title: t(
                                                                        'confirmationModal.title',
                                                                    ),
                                                                    content: t(
                                                                        'waitlist.actions.convertConfirm',
                                                                    ),
                                                                    confirm: () =>
                                                                        onConvertToBooking(row.id),
                                                                    maxWidth: 'xs',
                                                                    fullWidth: true,
                                                                });
                                                            }}
                                                        >
                                                            <SwapHorizIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={t('waitlist.actions.reminder')}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            disabled={reminderDisabled}
                                                            onClick={() => onSendReminder(row.id)}
                                                        >
                                                            <SendIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={t('common.delete')}>
                                                    <span>
                                                        <IconButton
                                                            disabled={deletingId === row.id}
                                                            onClick={() => {
                                                                showModal(MODAL_TYPE.CONFIRMATION, {
                                                                    title: t(
                                                                        'confirmationModal.title',
                                                                    ),
                                                                    content: t(
                                                                        'waitlist.actions.deleteConfirm',
                                                                    ),
                                                                    confirm: () => onDelete(row.id),
                                                                    maxWidth: 'xs',
                                                                    fullWidth: true,
                                                                });
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                        {!loading && rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Box sx={{ py: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {t(
                                                hasActiveFilters
                                                    ? 'waitlist.emptyFiltered'
                                                    : 'waitlist.empty',
                                            )}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : null}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                count={total || 0}
                rowsPerPage={limit}
                component="div"
                page={rows.length <= 0 ? 0 : page}
                onPageChange={(_, newPage) => handlePageChange(newPage, setParams)}
                rowsPerPageOptions={TABLE_ROW_COUNT_OPTIONS}
                onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setParams)}
                SelectProps={{
                    size: 'small',
                }}
                labelRowsPerPage={t('labelRowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
        </Grid2>
    );
}

export default WaitlistSubscriptionsTable;
